import React from "react"
import Dropzone, { FileRejection } from "react-dropzone"
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  IconWithBackground,
  ForwardIcon,
  createLucideIcon
} from "@carrotfertility/carotene-core"
import { Camera, Check } from "lucide-react"
import { compressPhotoIdImage } from "./compressImage"

const CameraIcon = createLucideIcon(Camera, "CameraIcon")
const CheckIcon = createLucideIcon(Check, "CheckIcon")

export interface PhotoIdImage {
  base64: string
  fileName: string
  contentType: "image/jpeg" | "image/png"
}

interface PhotoIdUploadProps {
  onSubmit: (images: { front: PhotoIdImage; back: PhotoIdImage }) => void | Promise<void>
  submitting?: boolean
}

// Validate the source type, then downscale/re-encode to a small JPEG (see compressImage) so the
// upload fits the BFF's Vercel request-body limit.
async function readAsPhotoIdImage(file: File): Promise<PhotoIdImage> {
  if (file.type !== "image/jpeg" && file.type !== "image/png") {
    throw new Error(`Unsupported image type: ${file.type || "unknown"}`)
  }
  return compressPhotoIdImage(file)
}

const AcceptablePhotoIdFileExtensions = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"]
}

// Cap on the SOURCE file the dropzone accepts (compressed down before upload). Generous so ordinary
// phone photos aren't rejected; the transport-sized cap is enforced post-compression in compressImage.
const MAX_ID_IMAGE_BYTES = 30 * 1024 * 1024 // 30 MB

function UploadDropzone({
  onDropAccepted,
  onDropRejected,
  label,
  helperText,
  accepted
}: {
  onDropAccepted: (files: File[]) => void
  onDropRejected: (fileRejections: FileRejection[]) => void
  label: string
  helperText: string
  accepted: boolean
}): JSX.Element {
  return (
    <Dropzone
      multiple={false}
      maxSize={MAX_ID_IMAGE_BYTES}
      onDropAccepted={onDropAccepted}
      onDropRejected={onDropRejected}
      accept={AcceptablePhotoIdFileExtensions}
    >
      {({ getRootProps, getInputProps }) => (
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.tokens.spacing.sm,
            backgroundColor: theme.palette.background.paper,
            inlineSize: "100%",
            borderRadius: theme.tokens.borderRadius.sm,
            cursor: "pointer",
            textAlign: "center",
            lineHeight: 1,
            paddingInline: theme.spacing(theme.tokens.spacing.md),
            transition: "all 0.3s cubic-bezier(0.8, 0, 0.2, 1)",
            paddingBlock: theme.tokens.spacing.xl,
            border: accepted
              ? `${theme.tokens.borderWidth.md} solid ${theme.palette.success.main}`
              : `${theme.tokens.borderWidth.md} dashed ${theme.palette.border.main}`
          })}
          {...getRootProps()}
        >
          {/* capture="environment" opens the rear camera on mobile */}
          <input {...getInputProps()} capture="environment" />
          <IconWithBackground
            icon={accepted ? CheckIcon : CameraIcon}
            variant="square"
            color={accepted ? "success" : "primary"}
            size="medium"
          />
          <Typography variant="h5">{label}</Typography>
          <Typography color="text.secondary">{helperText}</Typography>
        </Box>
      )}
    </Dropzone>
  )
}

function Slot({
  side,
  label,
  hint,
  value,
  onPick
}: {
  side: "front" | "back"
  label: string
  hint: string
  value: PhotoIdImage | null
  onPick: (img: PhotoIdImage) => void
}): JSX.Element {
  const [error, setError] = React.useState<string | null>(null)

  return (
    <Box
      sx={{
        flex: "1 1 16rem"
      }}
    >
      <Typography variant="h5" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xs })}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
        {hint}
      </Typography>
      <UploadDropzone
        accepted={value != null}
        label={value ? "Photo added" : `Add ${side} photo`}
        helperText={value ? value.fileName : "Drag & drop or click to upload"}
        onDropAccepted={async (files) => {
          setError(null)
          const file = files[0]
          if (!file) return
          try {
            onPick(await readAsPhotoIdImage(file))
          } catch {
            setError("Couldn't read that image — please try another file.")
          }
        }}
        onDropRejected={(rejections) => {
          setError(rejections[0]?.errors?.[0]?.message ?? "Unsupported file — use a JPG or PNG image.")
        }}
      />
      {error ? (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      ) : null}
    </Box>
  )
}

export function PhotoIdUpload({ onSubmit, submitting = false }: PhotoIdUploadProps): JSX.Element {
  const theme = useTheme()
  const [front, setFront] = React.useState<PhotoIdImage | null>(null)
  const [back, setBack] = React.useState<PhotoIdImage | null>(null)

  return (
    <Stack spacing={theme.tokens.spacing.md}>
      <Typography variant="h2">Upload your identification</Typography>
      <Typography variant="body1">
        Add a photo of the front and back of a government-issued ID. Your provider uses this to confirm your identity
        before the visit.
      </Typography>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: theme.tokens.spacing.md,
          marginBlockEnd: theme.tokens.spacing.md
        })}
      >
        <Slot
          side="front"
          label="Front of ID"
          hint="Driver's license, state ID, or passport."
          value={front}
          onPick={setFront}
        />
        <Slot side="back" label="Back of ID" hint="Make sure the barcode is readable." value={back} onPick={setBack} />
      </Box>
      <Button
        endIcon={<ForwardIcon />}
        variant="contained"
        disabled={!front || !back || submitting}
        onClick={() => {
          if (front && back) onSubmit({ front, back })
        }}
        sx={{ alignSelf: "flex-start" }}
      >
        {submitting ? "Uploading…" : "Continue"}
      </Button>
    </Stack>
  )
}
